import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  InputBase,
  Paper,
  Skeleton,
  TextField,
  useMediaQuery,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import AppTypography from "../typography/page";
import SearchIcon from "@mui/icons-material/Search";
import { ConfigProvider, Table, theme as antdTheme } from "antd";
import axios from "axios";
import CompactInfoChip from "../chips/CompactInfoChip";

const cardSx = {
  borderRadius: "8px",
  border: "1px solid #edf0f4",
  boxShadow: "0 14px 34px rgba(21, 29, 43, 0.08)",
  background: "#ffffff",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: 56,
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    transition: "box-shadow 160ms ease, background-color 160ms ease",
    "& fieldset": {
      borderColor: "#e2e7ef",
    },
    "&:hover fieldset": {
      borderColor: "#cbd4df",
    },
    "&.Mui-focused": {
      backgroundColor: "#ffffff",
      boxShadow: "0 0 0 3px rgba(221, 24, 59, 0.1)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#DD183B",
      borderWidth: 1,
    },
  },
  "& .MuiInputBase-input": {
    height: "56px !important",
    padding: "0 14px !important",
    margin: "0 !important",
    border: "0 !important",
    outline: "0 !important",
    background: "transparent !important",
    boxShadow: "none !important",
    fontFamily: "Poppins, sans-serif !important",
    fontSize: "14px !important",
    fontWeight: "500 !important",
    lineHeight: "56px !important",
    color: "#111827 !important",
  },
  "& .MuiInputLabel-root": {
    fontFamily: "Poppins, sans-serif !important",
    fontSize: "13px !important",
    fontWeight: "500 !important",
    color: "#64748b",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#DD183B",
  },
  "& .MuiAutocomplete-inputRoot": {
    padding: "0 38px 0 0 !important",
  },
  "& .MuiAutocomplete-input": {
    paddingLeft: "14px !important",
  },
  "& .MuiSelect-select": {
    height: "56px !important",
    minHeight: "56px !important",
    display: "flex",
    alignItems: "center",
    padding: "0 38px 0 14px !important",
  },
};

const tableContentGutter = {
  xs: "16px",
  md: "12px",
};

const DataTable = ({ isLoading, dataLocations, onNotify, isMobile }) => {
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);

  const [rows, setRows] = useState([]);
  const [loadingTable, setLoadingTable] = useState(false);

  const sectors = selectedLocation?.sektor ?? [];

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");

  const filteredTableData = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();

    if (!keyword) return rows;

    return rows.filter((item) => {
      return (
        item.lokasi?.toLowerCase().includes(keyword) ||
        item.sektor?.toLowerCase().includes(keyword) ||
        item.lapak?.toLowerCase().includes(keyword) ||
        item.status?.toLowerCase().includes(keyword)
      );
    });
  }, [rows, searchText]);

  const statusFilters = useMemo(() => {
    const uniqueStatus = [...new Set(rows.map((item) => item.status))];

    return uniqueStatus.map((status) => ({
      text: status,
      value: status,
    }));
  }, [rows]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingTable(true);
    if (!selectedLocation || !selectedSector) {
      onNotify({
        open: true,
        severity: "warning",
        message: "Silakan pilih Pasar dan Sektor.",
      });

      return;
    }

    try {
      const response = await axios.get(
        "https://www.pasarmanado.id/wp-json/pasarmanado/v1/lapak",
        {
          params: {
            pasar: selectedLocation.nama,
            sektor: selectedSector,
          },
        },
      );

      const result = response.data.map((item, index) => ({
        id: index + 1,

        lokasi: item.lokasi ?? "-",

        sektor: item.sektor ?? "-",

        lapak: item.lapak ?? "-",

        status: item.status ?? "-",

        keterangan: item.keterangan ?? "-",
      }));

      if (result.length === 0) {
        onNotify({
          open: true,
          severity: "error",
          message: "Data lapak tidak ditemukan.",
        });
        return;
      }

      if (result.length > 0) {
        onNotify({
          open: true,
          severity: "success",
          message: "Berhasil mengambil data lapak.",
        });
        setRows(result);
        setCurrentPage(1);
      }

      //   console.log("res lapak", result);
    } catch (err) {
      console.log(err);

      onNotify({
        open: true,
        severity: "error",
        message: "Gagal mengambil data lapak.",
      });
    } finally {
      setTimeout(() => {
        setLoadingTable(false);
      }, 1000);
    }
  };

  const onChange = (pagination) => {
    setCurrentPage(pagination.current);

    if (pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);

      // reset ke halaman pertama ketika page size berubah
      setCurrentPage(1);
    }
  };

  const columns = [
    {
      title: "No",
      width: 60,
      align: "center",
      render: (text, record, index) => (
        <Box
          sx={{
            width: 30,
            height: 30,
            mx: "auto",
            borderRadius: "8px",
            display: "grid",
            placeItems: "center",
            // backgroundColor: "#f2f5f9",
            color: "#566174",
            overflow: "hidden",
            "& p": {
              margin: "0 !important",
              padding: "0 !important",
              lineHeight: "1 !important",
            },
          }}
        >
          <AppTypography variant="tableMeta">
            {(currentPage - 1) * pageSize + index + 1}
          </AppTypography>
        </Box>
      ),
    },

    {
      title: "Nama Lokasi",
      dataIndex: "lokasi",
      key: "lokasi",
      width: isMobile ? 178 : undefined,
      render: (text) => (
        <AppTypography
          variant="tablePrimary"
          title={text}
          sx={isMobile ? { fontSize: 13, wordBreak: "normal" } : undefined}
        >
          {text}
        </AppTypography>
      ),
    },

    {
      title: "Sektor",
      dataIndex: "sektor",
      key: "sektor",
      width: isMobile ? 126 : undefined,
      render: (text) => (
        <AppTypography
          variant="tableSecondary"
          title={text}
          sx={isMobile ? { fontSize: 13, wordBreak: "normal" } : undefined}
        >
          {text}
        </AppTypography>
      ),
    },

    {
      title: "Nomor Lapak",
      dataIndex: "lapak",
      key: "lapak",
      align: "center",
      render: (text) => (
        <AppTypography variant="tableSecondary">{text}</AppTypography>
      ),
    },

    {
      title: "Status",
      dataIndex: "status",
      key: "status",

      filters: statusFilters,

      onFilter: (value, record) => record.status === value,

      render: (status) => (
        <CompactInfoChip
          label={status}
          color={status === "Sudah Terisi" ? "#C62828" : "#2E7D32"}
          sx={{ ml: 0, mr: 0 }}
        />
      ),
    },
  ];

  return (
    <form onSubmit={handleSubmit}>
      <Grid
        container
        mb={8}
        sx={{ width: "100%", maxWidth: "100%", overflowX: "hidden" }}
        spacing={{ xs: 3, md: 6 }}
      >
        <Grid container size={12}>
          <Paper
            elevation={3}
            sx={{
              ...cardSx,
              padding: { xs: "16px 12px", md: "22px" },
              overflow: "hidden",
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              "&, & *": {
                boxSizing: "border-box",
              },
              "& p, & span, & text": {
                fontFamily: "Poppins, sans-serif !important",
              },
              "& .apexcharts-tooltip": {
                fontFamily: "Poppins, sans-serif !important",
                borderRadius: "8px !important",
                boxShadow: "0 16px 38px rgba(15, 23, 42, 0.16) !important",
              },
              "& .apexcharts-tooltip-title": {
                fontFamily: "Poppins, sans-serif !important",
                fontWeight: "700 !important",
              },
            }}
          >
            <Grid
              container
              size={12}
              spacing={{ xs: 2, md: 3 }}
              alignItems="center"
            >
              {[1, 2, 3].map((item) => (
                <Grid
                  size={isMobile ? 12 : 4}
                  key={item}
                  sx={
                    {
                      //   display: item === 3 ? "flex" : "unset",
                      //   alignItems: item === 3 ? "center" : "flex-start",
                      //   justifyContent: item === 3 ? "center" : "flex-start",
                    }
                  }
                >
                  {isLoading ? (
                    <Skeleton variant="rounded" height={56} />
                  ) : item === 1 ? (
                    <Autocomplete
                      options={dataLocations}
                      value={selectedLocation}
                      getOptionLabel={(option) => option.nama}
                      isOptionEqualToValue={(option, value) =>
                        option.nama === value.nama
                      }
                      onChange={(e, value) => {
                        setSelectedLocation(value);
                        setSelectedSector(null);
                        setRows([]);
                        setCurrentPage(1);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Pilih Pasar"
                          sx={fieldSx}
                          disabled={dataLocations.length === 0 || loadingTable}
                        />
                      )}
                      disabled={dataLocations.length === 0 || loadingTable}
                    />
                  ) : item === 2 ? (
                    <>
                      <Autocomplete
                        options={sectors}
                        value={selectedSector}
                        onChange={(e, value) => {
                          setSelectedSector(value);
                          setRows([]);
                          setCurrentPage(1);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Pilih Sektor"
                            sx={fieldSx}
                            disabled={
                              !selectedLocation ||
                              sectors.length === 0 ||
                              loadingTable
                            }
                          />
                        )}
                        disabled={
                          !selectedLocation ||
                          sectors.length === 0 ||
                          loadingTable
                        }
                      />
                      <AppTypography
                        sx={{
                          fontSize: 13,
                          color: "#C62828",
                          fontWeight: 600,
                          mt: 1,
                          ml: 0.5,
                        }}
                      >
                        {selectedLocation
                          ? undefined
                          : "Silahkan pilih pasar terlebih dahulu."}
                      </AppTypography>
                    </>
                  ) : (
                    <Button
                      type="submit"
                      variant="contained"
                      color="error"
                      size="medium"
                      endIcon={<SearchIcon />}
                      sx={{
                        height: 52,
                        width: isMobile ? "100%" : 180,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontFamily: "Poppins, sans-serif",
                        fontWeight: "bold",
                        fontSize: "16px",
                        ml: isMobile ? 0 : 3,
                        boxShadow: "0 12px 24px rgba(221, 24, 59, 0.22)",
                        background:
                          "linear-gradient(135deg, #DD183B 0%, #B80F2C 100%)",
                        "&:hover": {
                          boxShadow: "0 14px 28px rgba(221, 24, 59, 0.28)",
                          background:
                            "linear-gradient(135deg, #c91435 0%, #9f0d26 100%)",
                        },
                      }}
                      disabled={loadingTable}
                    >
                      {loadingTable ? "Mencari data..." : "Cari Data"}
                    </Button>
                  )}
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* DATA TABLE */}
        <Grid size={12} mt={6}>
          <ConfigProvider
            theme={{
              algorithm: antdTheme.defaultAlgorithm,
              token: {
                borderRadius: 8,
                colorPrimary: "#DD183B",
                colorBorder: "#e8edf3",
                colorText: "#263142",
                fontFamily: "Poppins, sans-serif",
              },
              components: {
                Table: {
                  headerBg: "#f7f9fc",
                  headerColor: "#5b6678",
                  headerSplitColor: "#e8edf3",
                  rowHoverBg: "#fff7f8",
                  borderColor: "#edf0f4",
                },
                Input: {
                  activeBorderColor: "#DD183B",
                  hoverBorderColor: "#cbd4df",
                  activeShadow: "0 0 0 3px rgba(221, 24, 59, 0.1)",
                },
                Pagination: {
                  itemActiveBg: "#DD183B",
                  itemActiveBorderColor: "#DD183B",
                },
              },
            }}
          >
            <Paper
              elevation={3}
              sx={{
                ...cardSx,
                padding: { xs: "14px 0", md: "16px 0" },
                overflow: "hidden",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              {isLoading || loadingTable ? (
                <Skeleton
                  variant="rounded"
                  height={450}
                  sx={{ borderRadius: "8px" }}
                />
              ) : (
                <>
                  <Box
                    sx={{
                      px: tableContentGutter,
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                      boxSizing: "border-box",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        width: isMobile ? "100%" : 300,
                        maxWidth: "100%",
                        height: 44,
                        mb: "18px",
                        mt: "4px",
                      }}
                    >
                      <InputBase
                        value={searchText}
                        onChange={(e) => {
                          setSearchText(e.target.value);
                          setCurrentPage(1);
                        }}
                        placeholder="Cari lokasi, sektor, lapak atau status..."
                        aria-label="Cari data lapak"
                        sx={{
                          flex: 1,
                          height: 44,
                          border: "1px solid #d9e1ec",
                          borderRight: 0,
                          borderRadius: "8px 0 0 8px",
                          backgroundColor: "#ffffff",
                          boxShadow: "0 6px 16px rgba(15, 23, 42, 0.04)",
                          px: 1.5,
                          "& input": {
                            height: "44px !important",
                            padding: "0 !important",
                            margin: "0 !important",
                            border: "0 !important",
                            outline: "0 !important",
                            background: "transparent !important",
                            boxShadow: "none !important",
                            fontFamily: "Poppins, sans-serif",
                            fontSize: "14px !important",
                            fontWeight: "500 !important",
                            lineHeight: "44px !important",
                            color: "#111827 !important",
                          },
                          "& input::placeholder": {
                            color: "#8a96a8",
                            opacity: 1,
                          },
                          "&:focus-within": {
                            borderColor: "#DD183B",
                            boxShadow: "0 0 0 3px rgba(221, 24, 59, 0.08)",
                          },
                        }}
                      />
                      <IconButton
                        type="button"
                        aria-label="Cari data pangan"
                        sx={{
                          width: 48,
                          height: 44,
                          minWidth: 48,
                          padding: 0,
                          borderRadius: "0 8px 8px 0",
                          border: "1px solid #DD183B",
                          borderLeft: 0,
                          backgroundColor: "#DD183B",
                          color: "#ffffff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          lineHeight: 1,
                          "&:hover": {
                            backgroundColor: "#c91435",
                          },
                          "& svg": {
                            display: "block",
                          },
                        }}
                      >
                        <SearchIcon sx={{ fontSize: 23 }} />
                      </IconButton>
                    </Box>
                    <Box
                      sx={{
                        borderRadius: "8px",
                        border: "1px solid #edf0f4",
                        overflow: "hidden",
                        width: "100%",
                        maxWidth: "100%",
                        boxSizing: "border-box",
                        "& .ant-table": {
                          width: "100%",
                          maxWidth: "100%",
                          fontFamily: "Poppins, sans-serif",
                        },
                        "& .ant-table-container, & .ant-table-content": {
                          width: "100%",
                          maxWidth: "100%",
                          overflowX: isMobile
                            ? "auto !important"
                            : "hidden !important",
                          overflowY: "hidden !important",
                        },
                        "& .ant-table table": {
                          width: isMobile
                            ? "462px !important"
                            : "100% !important",
                          minWidth: isMobile ? "462px" : "unset",
                          tableLayout: isMobile ? "fixed" : "auto",
                        },
                        "& .ant-table-thead > tr > th": {
                          height: 48,
                          padding: isMobile ? "10px 8px" : "12px 16px",
                          fontSize: isMobile ? 12.5 : 13,
                          fontWeight: 800,
                          letterSpacing: 0,
                          textTransform: "uppercase",
                          lineHeight: 1.2,
                          color: "#435169",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                        "& .ant-table-tbody > tr > td": {
                          borderBottomColor: "#f0f3f7",
                          height: "auto !important",
                          minHeight: "unset !important",
                          padding: isMobile ? "10px 8px" : "14px 16px",
                          lineHeight: 1.45,
                          verticalAlign: "middle",
                          color: "#111827",
                          overflow: "hidden",
                        },
                        "& .ant-table p": {
                          margin: "0 !important",
                          padding: "0 !important",
                          fontFamily: "Poppins, sans-serif !important",
                        },
                        "& .ant-table-thead > tr > th:last-of-type, & .ant-table-tbody > tr > td:last-of-type":
                          {
                            paddingRight: isMobile
                              ? "10px !important"
                              : "18px !important",
                          },
                        "& .ant-table-thead > tr > th:first-of-type, & .ant-table-tbody > tr > td:first-of-type":
                          {
                            paddingLeft: isMobile
                              ? "10px !important"
                              : "18px !important",
                          },
                        "& .ant-table-tbody > tr:nth-of-type(even) > td": {
                          backgroundColor: "#fcfdff",
                        },
                        "& .ant-table-tbody > tr:hover > td": {
                          backgroundColor: "#fffafb !important",
                        },
                        "& .ant-table-row": {
                          height: "auto !important",
                        },
                        "& .ant-table-body table": {
                          borderCollapse: "separate",
                          borderSpacing: 0,
                        },
                        "& .ant-pagination": {
                          alignItems: "center",
                          justifyContent: isMobile ? "center" : "flex-end",
                          flexWrap: "wrap",
                          gap: "4px",
                          margin: isMobile ? "14px 8px 2px" : "16px 12px 4px",
                        },
                        "& .ant-pagination-total-text": {
                          flexBasis: isMobile ? "100%" : "auto",
                          textAlign: isMobile ? "center" : "left",
                          marginInlineEnd: isMobile
                            ? "0 !important"
                            : undefined,
                          fontSize: isMobile ? 12 : 14,
                        },
                        "& .ant-pagination-item": {
                          borderRadius: "8px",
                          minWidth: isMobile ? 32 : 34,
                          height: isMobile ? 32 : 34,
                          lineHeight: isMobile ? "30px" : "32px",
                        },
                        "& .ant-pagination-item-active": {
                          backgroundColor: "#DD183B !important",
                          borderColor: "#DD183B !important",
                        },
                        "& .ant-pagination-item-active a": {
                          color: "#ffffff !important",
                          fontWeight: "700 !important",
                        },
                        "& .ant-pagination-item:not(.ant-pagination-item-active) a":
                          {
                            color: "#263142",
                          },
                        "& .ant-pagination-prev, & .ant-pagination-next": {
                          minWidth: isMobile ? 32 : 34,
                          height: isMobile ? 32 : 34,
                          lineHeight: isMobile ? "32px" : "34px",
                        },
                      }}
                    >
                      <Table
                        rowKey={(record, index) =>
                          `${record.lokasi}-${record.sektor}-${record.lapak}-${index}`
                        }
                        columns={columns}
                        dataSource={filteredTableData}
                        onChange={onChange}
                        pagination={{
                          pageSize: pageSize,
                          current: currentPage,
                          showSizeChanger: !isMobile,
                          showLessItems: isMobile,
                          size: isMobile ? "small" : "default",
                          pageSizeOptions: [20, 30, 50, 100],
                          showTotal: (total, range) =>
                            `${range[0]}-${range[1]} dari ${total} data`,
                        }}
                      />
                    </Box>
                  </Box>
                </>
              )}
            </Paper>
          </ConfigProvider>
        </Grid>
      </Grid>
    </form>
  );
};

export default DataTable;
